import React, { useEffect, useState } from "react"
import LivescoringRow from "@/components/Livescoring/LivescoringRow"
import { useAutoAnimate } from "@formkit/auto-animate/react"
import { initSocketEvents } from "@/util/socket"
import { handleLivescoringSocketResponse } from "@/methods/livescoring_methods"

const LivescoringComponent = ({
    competitionName,
    calculation,
    clubName,
    livescoringData,
    section,
    rowLimit,
    competitionId,
    teeTime,
    isProduction,
    onUpdateCalculation,
    filters = null,
    positionCalculationKey,
    socketChannel,
    roundColumns: roundColumnsOverride = null
}) => {

    const roundNumber = livescoringData.roundNumber
    const compatibleRounds = livescoringData.compatibleRounds
    const roundId = livescoringData.roundId

    const podiumEnabled = "diapositiva" === section || "helper" === section
    const legacyShowTotal = 1 != roundNumber && compatibleRounds === true
    const normalizeRoundColumns = (data) => {
        if (data.roundColumns && data.roundColumns.length > 0) {
            return data.roundColumns
        }

        return [{
            roundId: data.roundId,
            roundNumber: data.roundNumber,
            roundName: `J${data.roundNumber}`,
            teeTime
        }]
    }
    const allRoundColumns = normalizeRoundColumns(livescoringData)
    const roundColumns = Array.isArray(roundColumnsOverride) ? roundColumnsOverride : allRoundColumns

    const [animationParent] = useAutoAnimate()

    const [days, setDays] = useState(livescoringData.rounds)
    const [sum, setSum] = useState(livescoringData.total)
    const [cardsOrder, setCardsOrder] = useState(livescoringData.keysOrder)
    const [activeRound, setActiveRound] = useState(livescoringData.roundId) // Este hook se creo para poder alternar entre jornadas mediante pestañas, pero para cambiar de jornada se hace por url asi que se deja asi por si se necesita en el futuro
    const [positionOrder, setPositionOrder] = useState(livescoringData.positionOrder)

    useEffect(() => {
        setDays(livescoringData.rounds)
        setSum(livescoringData.total)
        setCardsOrder(livescoringData.keysOrder)
        setPositionOrder(livescoringData.positionOrder)
    }, [livescoringData])

    const updateLivescoringData = (livescoringSocketData, socketRoundId = null) => {
        if (livescoringSocketData) {
            setDays(prevDays => allRoundColumns.length > 1 ? {...prevDays, ...livescoringSocketData[0]} : livescoringSocketData[0])

            if (allRoundColumns.length <= 1 || parseInt(socketRoundId) === parseInt(activeRound)) {
                setSum(livescoringSocketData[1])
                setCardsOrder(livescoringSocketData[2])
                setPositionOrder(livescoringSocketData[3])
            }
        }
    }

    useEffect(() => {
        /**
         * Cuando el componente livescoring está en la index no se suscribe al socket,
         * es el componente index el que lo hace y se encarga de controlar el socket a 
         * la vez para los livescoring que está mostrando
         */
        if ("index" === section) {
            return
        }

        const unsubscribeFromSocket = initSocketEvents({
            channel: socketChannel,
            // Refresh the HTTP snapshot when missed events could not be recovered.
            onReconnect: "livescoring" === section ? () => {
                if (typeof window.reloadLivescoringPage === "function") window.reloadLivescoringPage()
                else window.location.reload()
            } : undefined,
            action: (response) => handleLivescoringSocketResponse(
                response, 
                competitionId, 
                roundId, 
                isProduction, 
                calculation, 
                onUpdateCalculation, 
                updateLivescoringData,
                allRoundColumns.map(roundColumn => roundColumn.roundId)
            )
        })

        return () => {
            unsubscribeFromSocket()
        }
    }, [])

    const calculateIfIncludedInFilter = (filters, match) => {
        const matchPlayersIncludedInFilter = match.players.map(player => {
            const playerHandicap = Number(player.playerHandicap)
            return null === filters || (
                (
                    null === filters.gender
                    || player.gender === filters.gender
                )
                && (
                    (
                        null === filters.handicap.min
                        || null === filters.handicap.max
                    )
                    || (
                        playerHandicap >= filters.handicap.min
                        && playerHandicap <= filters.handicap.max
                    )
                )
                && (
                    0 === filters.tees.length
                    || filters.tees.includes(player.categoryId)
                )
            )
        })

        for (const playerIsIncludedInFilter of matchPlayersIncludedInFilter) {
            if (playerIsIncludedInFilter) {
                return true
            }
        }

        return false
    }

    const normalizeScore = (score) => {
        if (null === score || undefined === score) {
            return null
        }

        const parsedScore = Number(score)

        return Number.isNaN(parsedScore) ? score : parsedScore
    }

    const sumAvoidingNulls = (total, valueToSum) => {
        if (null === total && null === valueToSum) {
            return null
        }

        return (Number(total) || 0) + (Number(valueToSum) || 0)
    }

    const buildVisibleTotal = () => {
        if (roundColumns.length <= 1) {
            return sum
        }

        return roundColumns.reduce((totalByCard, roundColumn) => {
            const roundScores = days?.[roundColumn.roundId] || {}

            Object.keys(roundScores).forEach(cardOrder => {
                const match = roundScores[cardOrder]

                if (!totalByCard[cardOrder]) {
                    totalByCard[cardOrder] = {
                        ...match,
                        hcp: null,
                        scratch: null,
                        contraparHcp: null,
                        contraparScratch: null,
                        stablefordHcp: null,
                        stablefordScratch: null,
                        hoyos: 0
                    }
                }

                totalByCard[cardOrder] = {
                    ...totalByCard[cardOrder],
                    hcp: sumAvoidingNulls(totalByCard[cardOrder].hcp, match.hcp),
                    scratch: sumAvoidingNulls(totalByCard[cardOrder].scratch, match.scratch),
                    contraparHcp: sumAvoidingNulls(totalByCard[cardOrder].contraparHcp, match.contraparHcp),
                    contraparScratch: sumAvoidingNulls(totalByCard[cardOrder].contraparScratch, match.contraparScratch),
                    stablefordHcp: sumAvoidingNulls(totalByCard[cardOrder].stablefordHcp, match.stablefordHcp),
                    stablefordScratch: sumAvoidingNulls(totalByCard[cardOrder].stablefordScratch, match.stablefordScratch),
                    especial: match.especial ?? totalByCard[cardOrder].especial,
                    hoyos: Math.max(totalByCard[cardOrder].hoyos || 0, match.hoyos || 0)
                }
            })

            return totalByCard
        }, {})
    }

    const visibleTotal = buildVisibleTotal()

    const obtainScoreByPositionCalculationKey = (match) => {
        const isHandicapCalculation = positionCalculationKey.includes("H")
        const isTodayCalculation = positionCalculationKey.includes("today")
        const scoreKey = isHandicapCalculation ? "contraparHcp" : "contraparScratch"

        if (isTodayCalculation) {
            return normalizeScore(match[scoreKey])
        }

        const totalScore = visibleTotal?.[match.clave]?.[scoreKey]

        return normalizeScore(undefined === totalScore ? match[scoreKey] : totalScore)
    }

    const showTotal = roundColumns.length > 1 || (roundColumns.length === 1 && legacyShowTotal)
    const visibleRoundIds = roundColumns.map(roundColumn => String(roundColumn.roundId))
    const orderedCards = roundColumns.length > 1
        ? Object.keys(visibleTotal).sort((leftCardOrder, rightCardOrder) => {
            const scoreKey = positionCalculationKey.includes("H") ? "contraparHcp" : "contraparScratch"
            const leftScore = visibleTotal[leftCardOrder]?.[scoreKey]
            const rightScore = visibleTotal[rightCardOrder]?.[scoreKey]
            const leftSortableScore = null === leftScore || undefined === leftScore ? 1000 : Number(leftScore)
            const rightSortableScore = null === rightScore || undefined === rightScore ? 1000 : Number(rightScore)

            if (leftSortableScore !== rightSortableScore) {
                return leftSortableScore - rightSortableScore
            }

            return (visibleTotal[rightCardOrder]?.hoyos || 0) - (visibleTotal[leftCardOrder]?.hoyos || 0)
        })
        : (cardsOrder[positionCalculationKey] || []).map(cardOrder => String(cardOrder))
    const orderedCardSet = new Set(orderedCards)
    const orderedCardsWithVisibleRounds = roundColumns.length === 0 ? [] : roundColumns.reduce((accumulator, roundColumn) => {
        const roundScores = days?.[roundColumn.roundId] || {}
        Object.keys(roundScores).forEach(cardOrder => {
            const normalizedCardOrder = String(cardOrder)
            if (!orderedCardSet.has(normalizedCardOrder)) {
                orderedCardSet.add(normalizedCardOrder)
                accumulator.push(normalizedCardOrder)
            }
        })

        return accumulator
    }, [...orderedCards])

    const cardsIncludedInFilter = orderedCardsWithVisibleRounds.reduce((accumulator, cardOrder, index) => {
        const match = days?.[activeRound]?.[cardOrder] || visibleRoundIds
            .map(visibleRoundId => days?.[visibleRoundId]?.[cardOrder])
            .find(roundMatch => roundMatch)

        if (!match) {
            return accumulator
        }

        if (!calculateIfIncludedInFilter(filters, match)) {
            return accumulator
        }

        accumulator.push({ cardOrder, match, index })

        return accumulator
    }, [])

    const cardsToDisplay = 0 === rowLimit ? cardsIncludedInFilter : cardsIncludedInFilter.slice(0, rowLimit)

    const recalculatedPositionOrder = cardsToDisplay.reduce((accumulator, { cardOrder, match }, index) => {
        const currentScore = obtainScoreByPositionCalculationKey(match)
        const previousCard = cardsToDisplay[index - 1]
        const previousScore = previousCard ? obtainScoreByPositionCalculationKey(previousCard.match) : null
        const hasTiedWithPreviousScore = index > 0 && previousScore === currentScore

        accumulator[cardOrder] = hasTiedWithPreviousScore ? "" : index + 1

        return accumulator
    }, {})

    const podiumCards = podiumEnabled ? cardsToDisplay.filter(({ index }) => index < 3) : []
    const queueCards = podiumEnabled ? cardsToDisplay.filter(({ index }) => index >= 3) : cardsToDisplay

    const renderLivescoringRow = ({ cardOrder, match }) => (
        <LivescoringRow
            key={cardOrder}
            match={match}
            cardOrder={cardOrder}
            position={recalculatedPositionOrder[cardOrder]}
            positionOrder={positionOrder}
            roundNumber={livescoringData.roundNumber}
            sum={visibleTotal}
            compatibleRounds={livescoringData.compatibleRounds}
            calculation={calculation}
            section={section}
            competitionId={competitionId}
            teeTime={teeTime}
            positionCalculationKey={positionCalculationKey}
            roundColumns={roundColumns}
            days={days}
            showTotal={showTotal}
        />
    )

    return (
        <div className="react-livescoring">
            {
                Object.values(days).length > 0 ?
                    <div className="livescoringComponent">
                        <div className="livescoringComponentContent">
                            <div className="livTitle">{"index" === section ? competitionName : ''}</div>
                            {
                                "index" === section &&
                                    <div className="scClub">{ clubName }</div>
                            }
                            <div className={`liv-head ${showTotal ? "" : "oneRound"}`}>
                                <div className="autoScrollIndicator">
                                    <div className="autoScrollSteps" aria-hidden="true"></div>
                                    <button className="autoScrollResume" type="button">
                                        pausa
                                        <span className="autoScrollResumeProgress" aria-hidden="true"></span>
                                    </button>
                                </div>
                                {
                                    roundColumns.map(roundColumn => (
                                        <div key={roundColumn.roundId} className='liv-head-item tj'>{ roundColumn.roundName || `J${roundColumn.roundNumber}` }</div>
                                    ))
                                }
                                {
                                    showTotal &&
                                        <div className='liv-head-item tt'>T</div>
                                }
                            </div>
                            <div className='lista'>
                                <div className={'podium' + (cardsOrder.length > 12 ? ' separatorShadow' : '')} ref={animationParent}>
                                    { podiumCards.map(renderLivescoringRow) }
                                </div>
                                <div className='queue liveScroll' ref={animationParent}>
                                    { queueCards.map(renderLivescoringRow) }
                                </div>
                            </div>
                        </div>
                    </div>

                    :

                    <div className={'emptyLivescoring' + ("livescoring" === section ? " big" : "")}>
                        {
                            "index" === section &&
                                <h1>Esperando Livescoring</h1>
                        }
                    </div>
            }
        </div>
    )
}

export default LivescoringComponent
